import { useEffect, useState } from "react";
import { NEW_USER_OFFER_DURATION_MS } from "@/config/new-user-offer";
import { useUserStore } from "@/stores/user-store";

const storageKey = (userId: string) => `ct_new_user_offer_expires_${userId}`;

function readExpiry(userId: string): number | null {
  try {
    const raw = localStorage.getItem(storageKey(userId));
    if (!raw) return null;
    const n = Number(raw);
    return Number.isFinite(n) ? n : null;
  } catch {
    return null;
  }
}

function armOffer(userId: string): number {
  const expiresAt = Date.now() + NEW_USER_OFFER_DURATION_MS;
  try {
    localStorage.setItem(storageKey(userId), String(expiresAt));
  } catch {
    // Private browsing — offer still runs for this session via state.
  }
  return expiresAt;
}

function formatCountdown(totalSeconds: number): string {
  const m = Math.floor(totalSeconds / 60);
  const s = totalSeconds % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
}

/**
 * Starts a 15-minute countdown the first time a non-buyer hits billing or
 * the out-of-credits modal. Purely cosmetic — checkout still grants the
 * normal pack credits.
 */
export function useNewUserOffer(options?: { startTimer?: boolean }) {
  const startTimer = options?.startTimer ?? true;
  const { user, hasPurchased } = useUserStore();
  const userId = user?.user_id;
  const eligible = Boolean(userId && !hasPurchased);

  const [expiresAt, setExpiresAt] = useState<number | null>(null);
  const [secondsLeft, setSecondsLeft] = useState(0);

  useEffect(() => {
    if (!eligible || !userId) {
      setExpiresAt(null);
      setSecondsLeft(0);
      return;
    }

    let deadline = readExpiry(userId);
    if (!deadline && startTimer) {
      deadline = armOffer(userId);
    }
    if (!deadline) {
      setExpiresAt(null);
      setSecondsLeft(0);
      return;
    }
    setExpiresAt(deadline);
    setSecondsLeft(Math.max(0, Math.ceil((deadline - Date.now()) / 1000)));
  }, [eligible, userId, startTimer]);

  useEffect(() => {
    if (!expiresAt || !eligible) return;

    const tick = () => {
      const left = Math.max(0, Math.ceil((expiresAt - Date.now()) / 1000));
      setSecondsLeft(left);
    };

    tick();
    const id = window.setInterval(tick, 1000);
    return () => window.clearInterval(id);
  }, [expiresAt, eligible]);

  const isActive = eligible && secondsLeft > 0;

  return {
    isActive,
    secondsLeft,
    countdownLabel: formatCountdown(secondsLeft),
  };
}
